import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'next-i18next'

import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { Session } from '~/types/tutoring-api'
import { useModifySessionMutation } from '~/redux/apis/session-api'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import TimesIcon from '~/components/Icons/svgs/TimesIcon'

export default function CompletedSessionAddComment({
  currentComment,
  sessionId,
  setDisplayCommentTextarea,
}: {
  currentComment: Session['mentorComment']
  sessionId: number
  setDisplayCommentTextarea: Dispatch<SetStateAction<boolean>>
}): React.ReactElement {
  const { t } = useTranslation(['generics', 'tutorPanel'])
  const [comment, setComment] = useState(currentComment ?? '')
  const [commentError, setCommentError] = useState('')
  const [modifySession, { isSuccess, isError }] = useModifySessionMutation()

  const handleOnSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setCommentError('')
    const trimmedComment = comment.trim()

    if (trimmedComment.length > 5 && trimmedComment.length < 255) {
      await modifySession({ id: sessionId, mentorComment: comment })
    } else {
      setCommentError(t('tutorPanel:sessionComment.validationError'))
    }
  }

  return (
    <div className="space-x-2 flex items-start">
      <form className="w-full mb-2 space-y-2" onSubmit={handleOnSubmit}>
        <textarea
          className="w-full p-3 border border-gray-light rounded"
          maxLength={254}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('generics:addComment')}
          rows={3}
          value={comment}
        />
        <div className="flex justify-between">
          <CommentMessage commentError={commentError} isError={isError} isSuccess={isSuccess} />
          <ButtonPrimary size="x-small" type="submit">
            {t('generics:send')}
          </ButtonPrimary>
        </div>
      </form>
      <button aria-label={t('generics:ariaLabelButtons.closeWindow')} onClick={() => setDisplayCommentTextarea(false)}>
        <SVGIcon icon={TimesIcon} pathClassName="text-purple" size={20} />
      </button>
    </div>
  )
}

function CommentMessage({
  commentError,
  isError,
  isSuccess,
}: {
  commentError: string
  isError: boolean
  isSuccess: boolean
}): React.ReactElement {
  const { t } = useTranslation(['errors', 'tutorPanel'])
  const cx = 'text-xxs'

  switch (true) {
    case Boolean(commentError):
      return <span className={`text-red ${cx}`}>{commentError}</span>

    case isError:
      return <span className={`text-red ${cx}`}>{t('errors:unknown')}</span>

    case isSuccess:
      return <span className={`text-green-checkmark ${cx}`}>{t('tutorPanel:sessionComment.commentUpdated')}</span>

    default:
      return <span />
  }
}
